import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Compass, ChevronDown, Loader2, Search } from "lucide-react";
import { api } from "../../../lib/api";
import { apiErr } from "../../../lib/apiErr";
import { Chip, ROLES, SCOPES, domainName, useDomains } from "./skillUtil";

const sel = "input-ui w-auto text-xs";

function Result({ r }) {
  if (r.error === "invalid_query") return (
    <div className="rounded-xl bg-rose-50 p-3 text-xs text-rose-700" data-testid="discover-invalid">
      <p className="font-bold">Kueri tidak valid</p>
      {Object.entries(r.invalid || {}).map(([k, v]) => <p key={k} className="mt-1"><b>{k}</b>: "{String(v.given)}" — boleh: <span className="font-mono">{(v.allowed || []).join(", ")}</span></p>)}
    </div>
  );
  if (!r.items?.length) return <p className="rounded-xl bg-slate-50 p-3 text-xs text-slate-500" data-testid="discover-empty">{r.hint || "Tidak ada skill yang cocok."}</p>;
  return (
    <ol className="space-y-1.5" data-testid="discover-results">
      {r.items.map((s, i) => (
        <li key={s.id} className="flex flex-wrap items-center gap-2 rounded-xl border border-line p-2.5 text-xs" data-testid={`discover-item-${s.id}`}>
          <span className="font-mono text-[10px] text-slate-400">#{i + 1}</span>
          <Link to={`/skills/${s.id}`} className="font-bold text-slate-900 hover:text-brand">{s.name}</Link>
          <span className="font-mono text-[10px] text-slate-400">{s.id} · v{s.version}</span>
          <Chip tone="brand">{ROLES[s.role] || s.role}</Chip><Chip tone="violet">{domainName(s.domain)}</Chip>
          <span className="basis-full text-[11px] text-slate-500">{s.description}</span>
        </li>
      ))}
    </ol>
  );
}

export function DiscoverTester() {
  const domains = useDomains();
  const [open, setOpen] = useState(false); const [agents, setAgents] = useState(null);
  const [q, setQ] = useState({ role: "requirement", domain: "ALL", caller: "assistant", scope: "chat" });
  const [r, setR] = useState(null); const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
  const toggle = () => { setOpen(!open); if (!agents) api.get("/platform/agents").then((x) => setAgents(x.data.items || [])).catch(() => setAgents([])); };
  const run = async () => { setBusy(true); setErr(""); try { setR((await api.get("/platform/skills/discover", { params: q })).data); } catch (e) { setErr(apiErr(e, "Gagal menguji discovery")); setR(null); } finally { setBusy(false); } };
  const set = (p) => setQ((x) => ({ ...x, ...p }));
  return (
    <section className="aivora-card mb-4 p-4" data-testid="discover-tester">
      <button type="button" onClick={toggle} className="flex w-full items-center gap-2 text-left" data-testid="discover-toggle">
        <Compass size={15} className="text-brand" /><span className="flex-1 text-sm font-bold text-slate-900">Uji discovery <span className="font-normal text-slate-500">— simulasi <code className="font-mono text-[11px]">get_available_skill(role, domain)</code></span></span>
        <ChevronDown size={15} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          <p className="text-[11px] text-slate-500">Urutan cocok: domain persis → <b>ALL</b> (berlaku di semua domain) → <b>general</b> (fallback). Hanya skill aktif yang ditugaskan ke pemanggil & cocok cakupan; maks. 20.</p>
          <div className="flex flex-wrap items-center gap-2">
            <select value={q.role} onChange={(e) => set({ role: e.target.value })} className={sel} data-testid="discover-role">{Object.entries(ROLES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
            <select value={q.domain} onChange={(e) => set({ domain: e.target.value })} className={sel} data-testid="discover-domain">{domains.map((k) => <option key={k} value={k}>{domainName(k)}</option>)}</select>
            <select value={q.caller} onChange={(e) => set({ caller: e.target.value })} className={sel} data-testid="discover-caller"><option value="assistant">Asisten</option>{(agents || []).map((a) => <option key={a.id} value={`agent:${a.id}`}>{a.labels?.id || a.id}</option>)}</select>
            <select value={q.scope} onChange={(e) => set({ scope: e.target.value })} className={sel} data-testid="discover-scope">{Object.entries(SCOPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
            <button onClick={run} disabled={busy} className="btn-primary rounded-xl px-4 py-2 text-xs" data-testid="discover-run">{busy ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />} Uji</button>
          </div>
          {err && <p className="text-xs font-semibold text-rose-600" data-testid="discover-error">{err}</p>}
          {r && <Result r={r} />}
        </div>
      )}
    </section>
  );
}
